"use strict";
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _YearnOpportunity_internals;
Object.defineProperty(exports, "__esModule", { value: true });
exports.YearnOpportunity = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const chain_adapters_1 = require("@shapeshiftoss/chain-adapters");
const logger_1 = require("@shapeshiftoss/logger");
const isNil_1 = __importDefault(require("lodash/isNil"));
const toLower_1 = __importDefault(require("lodash/toLower"));
const web3_utils_1 = require("web3-utils");
const constants_1 = require("./constants");
const utils_1 = require("./utils");
const bignumber_1 = require("./utils/bignumber");
const feeMultiplier = Object.freeze({
    fast: 1,
    average: 0.8,
    slow: 0.5,
});
class YearnOpportunity {
    constructor(deps, vault) {
        var _a, _b, _c;
        _YearnOpportunity_internals.set(this, void 0);
        __classPrivateFieldSet(this, _YearnOpportunity_internals, {
            chainAdapter: deps.chainAdapter,
            dryRun: deps.dryRun,
            logger: (_a = deps.logger) !== null && _a !== void 0 ? _a : new logger_1.Logger({ name: 'Yearn Opportunity' }),
            routerContract: deps.contract,
            vault,
            web3: deps.web3,
            yearn: deps.yearnSdk,
        }, "f");
        this.id = (0, toLower_1.default)(vault.address);
        this.metadata = vault.metadata;
        this.displayName = vault.metadata.displayName || vault.name;
        this.version = vault.version;
        this.symbol = vault.symbol;
        this.apy = (0, bignumber_1.bnOrZero)((_b = vault.metadata.apy) === null || _b === void 0 ? void 0 : _b.net_apy);
        this.isNew = ((_c = vault.metadata.apy) === null || _c === void 0 ? void 0 : _c.type) === 'new';
        this.name = `${vault.metadata.displayName} ${vault.version}`;
        this.expired = vault.metadata.depositsDisabled || (0, bignumber_1.bnOrZero)(vault.metadata.depositLimit).lte(0);
        // @TODO TotalSupply from the API awas 0
        this.supply = (0, bignumber_1.bnOrZero)(vault.metadata.totalSupply);
        this.tvl = {
            balance: (0, bignumber_1.bnOrZero)(vault.underlyingTokenBalance.amount),
            balanceUsdc: (0, bignumber_1.bnOrZero)(vault.underlyingTokenBalance.amountUsdc),
            assetId: (0, caip_1.toAssetId)({
                chainId: 'eip155:1',
                assetNamespace: 'erc20',
                assetReference: vault.tokenId || vault.token,
            }),
        };
        this.underlyingAsset = {
            balance: (0, utils_1.bn)(0),
            assetId: (0, caip_1.toAssetId)({
                chainId: 'eip155:1',
                assetNamespace: 'erc20',
                assetReference: vault.tokenId || vault.token,
            }),
        };
        this.positionAsset = {
            balance: (0, utils_1.bn)(0),
            assetId: (0, caip_1.toAssetId)({
                chainId: 'eip155:1',
                assetNamespace: 'erc20',
                assetReference: this.id,
            }),
            underlyingPerPosition: (0, bignumber_1.bnOrZero)(vault.metadata.pricePerShare),
        };
        this.feeAsset = {
            assetId: 'eip155:1/slip44:60',
        };
    }
    checksumAddress(address) {
        return __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.utils.toChecksumAddress(address);
    }
    /**
     * From the token contract address and vault address, we need to get the vault id. The router
     * contract needs the vault id to know which vault it is dealing with when depositing, since it
     * takes a token address and a vault id.
     */
    async getVaultId({ underlyingAssetAddress, vaultAddress, }) {
        const numVaults = await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").routerContract.methods
            .numVaults(this.checksumAddress(underlyingAssetAddress))
            .call();
        let id = null;
        for (let i = 0; i <= numVaults && (0, isNil_1.default)(id); i++) {
            const result = await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").routerContract.methods
                .vaults(this.checksumAddress(underlyingAssetAddress), i)
                .call();
            if (result === this.checksumAddress(vaultAddress))
                id = i;
        }
        if ((0, isNil_1.default)(id))
            throw new Error(`Could not find vault id for token: ${underlyingAssetAddress} vault: ${vaultAddress}`);
        return id;
    }
    async signAndBroadcast(input) {
        const { bip44Params, wallet, tx, feePriority } = input;
        if (!bip44Params)
            throw new Error('bip44Params required for signAndBroadcast');
        const feeSpeed = feePriority ? feePriority : 'fast';
        const chainAdapter = __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").chainAdapter;
        const gasPrice = (0, web3_utils_1.numberToHex)((0, bignumber_1.bnOrZero)(tx.gasPrice).times(feeMultiplier[feeSpeed]).toString());
        const txToSign = {
            ...tx,
            gasPrice,
            // Gas limit safety factor of 50% to prevent out of gas errors on chain.
            gasLimit: (0, web3_utils_1.numberToHex)(tx.estimatedGas.times(1.5).integerValue().toString()),
            nonce: (0, web3_utils_1.numberToHex)(tx.nonce),
            value: (0, web3_utils_1.numberToHex)(tx.value),
            addressNList: (0, chain_adapters_1.toAddressNList)(bip44Params),
        };
        if (wallet.supportsOfflineSigning()) {
            const signedTx = await chainAdapter.signTransaction({ txToSign, wallet });
            if (__classPrivateFieldGet(this, _YearnOpportunity_internals, "f").dryRun)
                return signedTx;
            return chainAdapter.broadcastTransaction(signedTx);
        }
        else if (wallet.supportsBroadcast() && chainAdapter.signAndBroadcastTransaction) {
            if (__classPrivateFieldGet(this, _YearnOpportunity_internals, "f").dryRun) {
                throw new Error(`Cannot perform a dry run with wallet of type ${wallet.getVendor()}`);
            }
            return chainAdapter.signAndBroadcastTransaction({ txToSign, wallet });
        }
        else {
            throw new Error('Invalid HDWallet configuration');
        }
    }
    async prepareDeposit(input) {
        const { address, amount } = input;
        // In order to properly earn affiliate revenue, we must deposit to the vault through the SS
        // router contract. This is not necessary for withdraws. We can withdraw directly from the vault
        // without affecting the DAOs affiliate revenue.
        const tokenChecksum = __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.utils.toChecksumAddress(__classPrivateFieldGet(this, _YearnOpportunity_internals, "f").vault.tokenId);
        const userChecksum = __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.utils.toChecksumAddress(address);
        const vaultIndex = await this.getVaultId({
            underlyingAssetAddress: __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").vault.tokenId,
            vaultAddress: __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").vault.address,
        });
        const preDeposit = await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").routerContract.methods.deposit(tokenChecksum, userChecksum, amount.toString(), vaultIndex);
        const data = await preDeposit.encodeABI({ from: address });
        const estimatedGas = (0, bignumber_1.bnOrZero)(await preDeposit.estimateGas({ from: address }));
        const nonce = await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.getTransactionCount(address);
        const gasPrice = (0, bignumber_1.bnOrZero)(await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.getGasPrice());
        return {
            chainId: 1,
            data,
            estimatedGas,
            gasPrice,
            nonce: String(nonce),
            to: constants_1.ssRouterContractAddress,
            value: '0',
        };
    }
    async prepareWithdrawal(input) {
        const { address, amount } = input;
        // We use the vault directly to withdraw the vault tokens. There is no benefit to the DAO to use
        // the router to withdraw funds and there is an extra approval required for the user if we
        // withdrew from the vault using the shapeshift router. Affiliate fees for SS are the same
        // either way. For this reason, we simply withdraw from the vault directly.
        const vaultContract = new (__classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.Contract)(constants_1.yv2VaultAbi, this.id);
        const preWithdraw = await vaultContract.methods.withdraw(amount.toString(), address);
        const data = await preWithdraw.encodeABI({ from: address });
        const estimatedGas = (0, bignumber_1.bnOrZero)(await preWithdraw.estimateGas({ from: address }));
        const nonce = await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.getTransactionCount(address);
        const gasPrice = (0, bignumber_1.bnOrZero)(await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.getGasPrice());
        return {
            chainId: 1,
            data,
            estimatedGas,
            gasPrice,
            nonce: String(nonce),
            to: this.id,
            value: '0',
        };
    }
    async allowance(address) {
        const depositTokenContract = new (__classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.Contract)(constants_1.erc20Abi, __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").vault.tokenId);
        const allowance = await depositTokenContract.methods
            .allowance(address, __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").routerContract.options.address)
            .call();
        return (0, bignumber_1.bnOrZero)(allowance);
    }
    async prepareApprove(address, amount) {
        const depositTokenContract = new (__classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.Contract)(constants_1.erc20Abi, __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").vault.tokenId);
        const preApprove = await depositTokenContract.methods.approve(constants_1.ssRouterContractAddress, amount ? (0, web3_utils_1.numberToHex)((0, bignumber_1.bnOrZero)(amount).toString()) : constants_1.MAX_ALLOWANCE);
        const data = await preApprove.encodeABI({ from: address });
        const estimatedGas = (0, bignumber_1.bnOrZero)(await preApprove.estimateGas({ from: address }));
        const nonce = await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.getTransactionCount(address);
        const gasPrice = (0, bignumber_1.bnOrZero)(await __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").web3.eth.getGasPrice());
        return {
            chainId: 1,
            data,
            estimatedGas,
            gasPrice,
            nonce: String(nonce),
            to: __classPrivateFieldGet(this, _YearnOpportunity_internals, "f").vault.tokenId,
            value: '0',
        };
    }
    /**
     * This just makes the logging in the CLI easier to read
     * @returns
     */
    [(_YearnOpportunity_internals = new WeakMap(), Symbol.for('nodejs.util.inspect.custom'))]() {
        return {
            id: this.id,
            metadata: this.metadata,
            displayName: this.displayName,
            apy: this.apy.toString(),
            // @TODO TotalSupply from the API awas 0
            supply: this.supply.toString(),
            tvl: {
                assetId: this.tvl.assetId,
                balance: this.tvl.balance.toString(),
            },
            underlyingAsset: {
                balance: this.underlyingAsset.balance.toString(),
                assetId: this.underlyingAsset.assetId,
            },
            positionAsset: {
                balance: this.positionAsset.balance.toString(),
                assetId: this.positionAsset.assetId,
                price: this.positionAsset.underlyingPerPosition.toString(),
            },
        };
    }
}
exports.YearnOpportunity = YearnOpportunity;
